/**
 * FileUploader Component
 * ======================
 * Upload CSV or XML files with automatic type detection and category extraction.
 * Shows file info prominently when loaded, with small upload button for replacement.
 */

import React, { useRef, useState } from 'react';
import { isMultiCategoryCSV, extractCategories, getAllHeaders, extractCategoryData } from '../utils/categoryParser';

function FileUploader({ currentFile, categoryCount, categoryMeta, rawRows, onUpload }) {
  const fileInputRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Calculate total racer count from categories
  const getTotalRacerCount = () => {
    if (!categoryMeta || !rawRows || categoryMeta.length === 0) {
      // For single-category files, we might have the data directly
      return null;
    }
    let total = 0;
    for (const meta of categoryMeta) {
      try {
        const data = extractCategoryData(rawRows, meta.startIndex);
        total += data.length;
      } catch {
        // Ignore errors
      }
    }
    return total;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setError(null);

    try {
      const fileName = file.name.toLowerCase();
      
      if (fileName.endsWith('.csv')) {
        await parseCSV(file);
      } else if (fileName.endsWith('.xml')) {
        await parseXML(file);
      } else {
        throw new Error('Unsupported file type. Please upload a CSV or XML file.');
      }
    } catch (err) {
      console.error('File parse error:', err);
      setError(err.message);
    } finally {
      setIsProcessing(false);
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const parseCSV = async (file) => {
    const text = await file.text();
    const lines = text.split('\n');
    
    if (lines.length < 2) {
      throw new Error('CSV file is empty or has no data rows.');
    }

    // Parse into raw rows (arrays)
    const rawRows = [];
    for (const line of lines) {
      if (line.trim() === '') continue;
      const values = parseCSVLine(line);
      rawRows.push(values);
    }

    if (rawRows.length < 2) {
      throw new Error('CSV file has no data rows.');
    }

    // Check for multi-category format
    const hasCategories = isMultiCategoryCSV(rawRows);
    
    if (hasCategories) {
      const categories = extractCategories(rawRows);
      const headers = getAllHeaders(rawRows);
      
      console.log('Multi-category CSV detected');
      console.log('Categories found:', categories);
      console.log('Headers:', headers);
      
      onUpload({
        fileName: file.name,
        rawRows, // Store raw rows for later category extraction
        headers,
        categories: categories.map(c => c.name),
        categoryMeta: categories, // Include startIndex for each category
        isMultiCategory: true,
      });
    } else {
      // Single category - convert to objects
      const headers = rawRows[0];
      const data = [];
      
      for (let i = 1; i < rawRows.length; i++) {
        const row = {};
        headers.forEach((header, idx) => {
          if (header && header.trim()) {
            row[header.trim()] = (rawRows[i][idx] || '').trim();
          }
        });
        // Only add rows with some data
        if (Object.values(row).some(v => v)) {
          data.push(row);
        }
      }
      
      onUpload({
        fileName: file.name,
        headers: headers.filter(h => h && h.trim()),
        data,
        categories: [],
        isMultiCategory: false,
        racerCount: data.length,
      });
    }
  };

  const parseCSVLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());

    return values;
  };

  const parseXML = async (file) => {
    const text = await file.text();
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/xml');
    
    // Check for parse errors
    const parseError = doc.querySelector('parsererror');
    if (parseError) {
      throw new Error('Invalid XML file.');
    }

    // Try to extract data from common FIS/USSA XML structures
    const competitors = doc.querySelectorAll('Competitor, competitor, Racer, racer');
    
    if (competitors.length === 0) {
      throw new Error('No competitor data found in XML. Please upload a results XML file.');
    }

    const data = [];
    const headerSet = new Set();

    competitors.forEach((comp) => {
      const row = {};
      
      // Extract all child elements as fields
      Array.from(comp.children).forEach((child) => {
        const key = child.tagName;
        const value = child.textContent;
        row[key] = value;
        headerSet.add(key);
      });
      
      // Also check attributes
      Array.from(comp.attributes).forEach((attr) => {
        row[attr.name] = attr.value;
        headerSet.add(attr.name);
      });
      
      data.push(row);
    });

    onUpload({
      fileName: file.name,
      headers: Array.from(headerSet),
      data,
      categories: [],
      isMultiCategory: false,
      racerCount: data.length,
    });
  };

  const handleButtonClick = () => {
    if (fileInputRef.current && !isProcessing) {
      fileInputRef.current.click();
    }
  };

  const totalRacers = getTotalRacerCount();
  const hasFile = !!currentFile;

  return (
    <div className="file-uploader">
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xml"
        onChange={handleFileSelect}
        disabled={isProcessing}
        style={{ display: 'none' }}
      />

      {hasFile ? (
        // File is loaded - show info prominently
        <div className="file-loaded-state">
          <div className="file-info-box">
            <div className="file-icon">📄</div>
            <div className="file-details">
              <div className="file-name">{currentFile}</div>
              <div className="file-stats">
                {categoryCount > 0 ? (
                  <>
                    <span className="stat"><strong>{categoryCount}</strong> categories</span>
                    {totalRacers !== null && (
                      <span className="stat"><strong>{totalRacers}</strong> racers</span>
                    )}
                  </>
                ) : (
                  <span className="stat">Single category file</span>
                )}
              </div>
            </div>
            <button 
              type="button"
              className="upload-btn-small"
              onClick={handleButtonClick}
              disabled={isProcessing}
              title="Upload different file"
            >
              {isProcessing ? '⏳' : '📁 New CSV File'}
            </button>
          </div>
        </div>
      ) : (
        // No file - show full upload area
        <div className="upload-area">
          <button 
            type="button"
            className="upload-btn"
            onClick={handleButtonClick}
            disabled={isProcessing}
          >
            {isProcessing ? '⏳ Processing...' : '📁 Choose CSV or XML File'}
          </button>
          <p className="hint">
            Supports CSV (with categories) and XML (for re-importing previous results)
          </p>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
    </div>
  );
}

export default FileUploader;
